import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { GrowthServiceFeedback, GrowthSuccessPlan } from './growth-service.service';

export interface Customer360ServiceSummary {
  healthScore: number | null;
  activeSuccessPlan: GrowthSuccessPlan | null;
  openCaseLinkCount: number;
  openRetentionPlayCount: number;
  latestFeedback: GrowthServiceFeedback | null;
}

export interface GrowthLeadResponse {
  id: string;
  tenantId: string;
  leadNumber: string;
  partyId: string | null;
  customerAccountId: string | null;
  source: string;
  channelKey: string;
  status: string;
  score: number | null;
  scoreReasonJson: string;
  consentPurposeKey: string | null;
  legalBasis: string | null;
  contentAssetId: string | null;
  engagementEventId: string | null;
  contentFeedbackId: string | null;
  campaignId: string | null;
  segmentId: string | null;
  externalSourceKey: string | null;
  externalSourceId: string | null;
  salesHandoffId: string | null;
  qualifiedAtUtc: string | null;
  handedOffAtUtc: string | null;
  correlationId: string;
  idempotencyKey: string;
  createdAtUtc: string;
}

export interface GrowthSegmentResponse {
  id: string;
  tenantId: string;
  key: string;
  name: string;
  description: string | null;
  ruleDefinitionJson: string;
  evaluationMode: string;
  status: string;
  lastEvaluatedAtUtc: string | null;
}

export interface GrowthSegmentMemberResponse {
  id: string;
  segmentId: string;
  partyId: string;
  reasonJson: string;
  evaluatedAtUtc: string;
}

export interface GrowthActivityResponse {
  id: string;
  tenantId: string;
  partyId: string | null;
  leadId: string | null;
  activityType: string;
  channelKey: string | null;
  source: string;
  occurredAtUtc: string;
  payloadJson: string;
  contentAssetId: string | null;
  campaignId: string | null;
  engagementEventId: string | null;
  correlationId: string;
}

export interface Customer360Response {
  party: { partyId: string; displayName: string; legalName: string | null; isReferenceOnly: boolean } | null;
  growthContext: {
    id: string;
    partyId: string;
    lifecycleStage: string;
    primaryIndustryKey: string | null;
    engagementScore: number | null;
    revenueScore: number | null;
  } | null;
  contactProfile: {
    lifecycleStage: string;
    tagsJson: string;
    consentSummaryJson: string;
    preferredChannel: string | null;
    lastActivityAtUtc: string | null;
  } | null;
  leads: GrowthLeadResponse[];
  segmentMemberships: GrowthSegmentMemberResponse[];
  recentActivities: GrowthActivityResponse[];
  loyalty: { accounts: { programId: string; programKey: string; programName: string; pointsBalance: number; tierKey: string | null }[] };
  sales: { mode: string; openOpportunityCount: number; latestOpportunityRef: string | null; note: string };
  intelligence: { clv: number | null; churnRisk: number | null; availabilityNote: string };
  service?: Customer360ServiceSummary;
}

export interface GrowthLoyaltyProgramResponse {
  id: string;
  tenantId: string;
  programKey: string;
  name: string;
  tierRulesJson: string;
  pointsRulesJson: string;
  status: string;
}

export interface SalesHandoffResultResponse {
  lead: GrowthLeadResponse;
  handoff: { salesHandoffId: string; status: string; externalReference: string | null; isIdempotentReplay: boolean };
}

@Injectable({ providedIn: 'root' })
export class GrowthCrmService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = environment.apiBaseUrl;

  listLeads(filter?: {
    status?: string;
    source?: string;
    channelKey?: string;
    minScore?: number;
    maxScore?: number;
    contentAssetId?: string;
    campaignId?: string;
    partyId?: string;
  }): Observable<GrowthLeadResponse[]> {
    let params = new HttpParams();
    if (filter?.status) params = params.set('status', filter.status);
    if (filter?.source) params = params.set('source', filter.source);
    if (filter?.channelKey) params = params.set('channelKey', filter.channelKey);
    if (filter?.minScore != null) params = params.set('minScore', filter.minScore);
    if (filter?.maxScore != null) params = params.set('maxScore', filter.maxScore);
    if (filter?.contentAssetId) params = params.set('contentAssetId', filter.contentAssetId);
    if (filter?.campaignId) params = params.set('campaignId', filter.campaignId);
    if (filter?.partyId) params = params.set('partyId', filter.partyId);
    return this.http.get<GrowthLeadResponse[]>(`${this.apiBaseUrl}/api/v1/growth/crm/leads`, { params });
  }

  getLead(id: string): Observable<GrowthLeadResponse> {
    return this.http.get<GrowthLeadResponse>(`${this.apiBaseUrl}/api/v1/growth/crm/leads/${id}`);
  }

  createLead(payload: {
    source: string;
    channelKey: string;
    partyId?: string | null;
    consentPurposeKey?: string | null;
    contentAssetId?: string | null;
    engagementEventId?: string | null;
    campaignId?: string | null;
    correlationId?: string | null;
    idempotencyKey?: string | null;
    payloadJson?: string | null;
  }): Observable<GrowthLeadResponse> {
    return this.http.post<GrowthLeadResponse>(`${this.apiBaseUrl}/api/v1/growth/crm/leads`, payload);
  }

  scoreLead(id: string, score: number, scoreReasonJson?: string): Observable<GrowthLeadResponse> {
    return this.http.post<GrowthLeadResponse>(`${this.apiBaseUrl}/api/v1/growth/crm/leads/${id}/score`, { score, scoreReasonJson });
  }

  qualifyLead(id: string): Observable<GrowthLeadResponse> {
    return this.http.post<GrowthLeadResponse>(`${this.apiBaseUrl}/api/v1/growth/crm/leads/${id}/qualify`, null);
  }

  disqualifyLead(id: string): Observable<GrowthLeadResponse> {
    return this.http.post<GrowthLeadResponse>(`${this.apiBaseUrl}/api/v1/growth/crm/leads/${id}/disqualify`, null);
  }

  handoffLead(id: string, attempt: number): Observable<SalesHandoffResultResponse> {
    return this.http.post<SalesHandoffResultResponse>(`${this.apiBaseUrl}/api/v1/growth/crm/leads/${id}/handoff-sales`, { attempt });
  }

  listSegments(): Observable<GrowthSegmentResponse[]> {
    return this.http.get<GrowthSegmentResponse[]>(`${this.apiBaseUrl}/api/v1/growth/crm/segments`);
  }

  createSegment(payload: {
    key: string;
    name: string;
    description?: string | null;
    ruleDefinitionJson: string;
    evaluationMode?: string;
    status?: string;
  }): Observable<GrowthSegmentResponse> {
    return this.http.post<GrowthSegmentResponse>(`${this.apiBaseUrl}/api/v1/growth/crm/segments`, payload);
  }

  evaluateSegment(id: string): Observable<GrowthSegmentMemberResponse[]> {
    return this.http.post<GrowthSegmentMemberResponse[]>(`${this.apiBaseUrl}/api/v1/growth/crm/segments/${id}/evaluate`, null);
  }

  listSegmentMembers(id: string): Observable<GrowthSegmentMemberResponse[]> {
    return this.http.get<GrowthSegmentMemberResponse[]>(`${this.apiBaseUrl}/api/v1/growth/crm/segments/${id}/members`);
  }

  getCustomer360(partyId: string): Observable<Customer360Response> {
    return this.http.get<Customer360Response>(`${this.apiBaseUrl}/api/v1/growth/crm/customer360/${partyId}`);
  }

  listLoyaltyPrograms(): Observable<GrowthLoyaltyProgramResponse[]> {
    return this.http.get<GrowthLoyaltyProgramResponse[]>(`${this.apiBaseUrl}/api/v1/growth/crm/loyalty/programs`);
  }

  createLoyaltyProgram(payload: {
    programKey: string;
    name: string;
    tierRulesJson: string;
    pointsRulesJson: string;
    status?: string;
  }): Observable<GrowthLoyaltyProgramResponse> {
    return this.http.post<GrowthLoyaltyProgramResponse>(`${this.apiBaseUrl}/api/v1/growth/crm/loyalty/programs`, payload);
  }
}
